import { useEffect, useState } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Signature', href: '#signature' },
    { label: 'Plans', href: '#plans' },
    { label: 'Benefits', href: '#benefits' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <>
      {/* NAVBAR */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-2xl border-b border-gray-100/80'
            : 'bg-transparent'
        }`}
      >
        <nav className="mx-auto px-6 lg:px-16">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <a href="/" className="flex items-center gap-3 flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/f49b2cbcc747a8cfb41259023b1a15e5.png"
                alt="Med-Care | Signature™"
                className="h-9 sm:h-11 w-auto"
              />
            </a>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`text-sm font-medium tracking-wide transition-all duration-200 whitespace-nowrap hover:text-teal-600 ${
                    scrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:702-723-0303"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-2 ${
                  scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white/80 hover:text-white'
                }`}
              >
                <i className="ri-phone-line text-base"></i>
                702.723.0303
              </a>
              <a
                href="#contact"
                className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Get Started
              </a>
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block py-3 text-sm font-medium text-gray-700 hover:text-teal-600 border-b border-gray-50 transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <a href="tel:702-723-0303" className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <i className="ri-phone-line text-teal-600"></i> 702.723.0303
              </a>
              <a href="#contact" className="block text-center py-3 bg-teal-600 text-white text-sm font-semibold rounded-lg whitespace-nowrap cursor-pointer">
                Get Started
              </a>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b9f62902dd2ac83be028725f667bd0eb.jpg Vegas Nevada luxury premium medical clinic building exterior at golden sunset hour with stunning desert landscape panoramic view modern glass architecture sleek contemporary healthcare facility with Las Vegas Strip city lights glowing in background dramatic sky warm amber tones upscale affluent medical center Nevada desert mountain backdrop&width=1440&height=900&seq=hero-vegas-mc-2026&orientation=landscape"
            alt="Med-Care Las Vegas Clinic"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2818]/85 via-[#0A2818]/65 to-[#0A2818]/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-16 py-32 lg:py-40">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-500/20 border border-teal-400/30 rounded-full mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></span>
              <span className="text-teal-300 text-xs font-semibold tracking-widest uppercase">Nevada Residents Only</span>
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6">
              MED-CARE<br />
              <span className="text-teal-400">SIGNATURE™</span>
            </h1>

            <p className="text-lg sm:text-xl text-white/80 font-light mb-3 leading-relaxed">
              Precision Care. Personal Access. Elevated Medicine.
            </p>
            <p className="text-sm sm:text-base text-white/60 mb-10 leading-relaxed max-w-lg">
              This is not a membership. This is your{' '}
              <strong className="text-white/80 font-semibold">Signature Standard of Care</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-14">
              <a
                href="#plans"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-file-list-3-line"></i>
                View Plans
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold rounded-lg backdrop-blur-sm transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-phone-line"></i>
                Contact Us
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              {[
                { value: '$99', label: 'Per Month' },
                { value: '3-Month', label: 'Min. Commitment' },
                { value: 'Unlimited', label: 'Telehealth' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span className="text-2xl sm:text-3xl font-bold text-teal-400 font-serif-display">{stat.value}</span>
                  <span className="text-xs sm:text-sm text-white/60 mt-0.5">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <a
            href="#signature"
            className="flex flex-col items-center gap-2 text-white/50 hover:text-white/80 transition-colors cursor-pointer group"
          >
            <span className="text-xs font-medium tracking-widest uppercase">Discover</span>
            <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent group-hover:from-white/70 transition-colors"></div>
          </a>
        </div>
      </section>
    </>
  );
}
